import {
    Box,
    capitalize,
    Chip,
    IconButton,
    Paper,
    Stack,
    Typography,
} from "@mui/material";
import React, {useCallback, useEffect, useMemo, useState} from "react";
import {GridColDef} from "@mui/x-data-grid";
import {
    DataLoader,
    DataRequest,
    DataRequestState,
    useDataRequest,
} from "../data";
import {useT} from "../../i18n";
import DataTable from "../data/DataTable";
import {useGetApi} from "../../client";
import {Timestamp} from "../../api";
import TextIncreaseIcon from "@mui/icons-material/TextIncrease";
import SpellcheckIcon from "@mui/icons-material/Spellcheck";
import PlaylistAddCheckCircleIcon from "@mui/icons-material/PlaylistAddCheckCircle";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import DatevProtokolleButton from "../rechnungen/DatevProtokolleButton";
import PaidIcon from "@mui/icons-material/Paid";
import CloseIcon from "@mui/icons-material/Close";
import {useNavigate} from "react-router";
import {Block} from "@mui/icons-material";
import {formatAsMoney} from "../../formatterHelpers";
import useCustomFilters from "../data/useCustomFilters";
import SearchField from "../SearchField";

export type ZahllaufRechnungenModalFilter = {
    zahllaufId: string;
    search?: string;
    filtered?: string;
};

export type EingangsrechnungenProps = {
    id: string;
    dateiname?: string;
    dateiErstelltAm?: Timestamp;
    rechnungsNummer?: string;
    rechnungsDatum?: Timestamp;
    baustellenId?: string;
    baustellenNamen?: string;
    baustellenNummern?: string;
    baustellenBezeichnungen?: string;
    prueferId?: string;
    lieferantId?: string;
    lieferantName?: string;
    geloeschtAm?: Timestamp;
    geloeschtVon?: string;
    nettoBetrag?: number;
    bruttoBetrag?: number;
    prueferNamen?: string;
    belegStatus?: string;
    pruefStatus?: string;
    belegTyp?: string;
    steuernummer?: string;
    umsatzsteuerId?: string;
    prueferIds?: string;
    skontoDatum?: Timestamp;
    faelligOhneSkonto?: Timestamp;
    steuer?: number;
    skontoProzent?: number;
    rechnungsBetrag?: number;
    rechnungsbetragAbweichend?: boolean;
    zahllaufId?: string;
    zahlungkw?: string;
    zahllaufBezahlt?: boolean;
    skontierfaehigerBetrag?: number;
    skontoBetrag?: number;
    iban?: string;
    kundenNummer?: string;
};

export type Props = Omit<DataRequest<ZahllaufRechnungenModalFilter>, "filter"> &
    Partial<Pick<DataRequest<ZahllaufRechnungenModalFilter>, "filter">> & {
    zahllaufId: string;
};

const tableName: string = "zahllauf-rechnungen"

function ZahllaufRechnungenModal({
                                     setOpenedModal,
                                     zahllaufId,
                                     ...input
                                 }: { setOpenedModal: (value: boolean) => void } & Props) {
    const {t: translation} = useT("zahllauf");
    const [filteredState, setFilteredState] = useState("");
    const request = useDataRequest<ZahllaufRechnungenModalFilter>({
        filter: {zahllaufId: zahllaufId},
        ...input,
    });

    return (
        <>
            <Paper elevation={1} sx={{padding: 3}}>
                <IconButton
                    sx={{float: "right"}}
                    onClick={() => setOpenedModal(false)}
                >
                    <CloseIcon/>
                </IconButton>
                <Stack direction={"column"} spacing={2}>
                    <Typography variant="h5">{capitalize(translation("invoices"))}</Typography>
                    <FilterComp
                        request={request}
                        filteredStateFilter={filteredState}
                    />
                    <ZahllaufRechnungenResults
                        setOpenedModal={setOpenedModal}
                        request={request}
                        setFilteredState={setFilteredState}
                    />
                </Stack>
            </Paper>
        </>
    );
}

function FilterComp({
                        request,
                        filteredStateFilter,
                    }: {
    request: DataRequestState<ZahllaufRechnungenModalFilter>;
    filteredStateFilter: string;
}) {
    const {setFilter} = request;
    const {currentCustomFilter, setCustomField} = useCustomFilters<ZahllaufRechnungenModalFilter>({
        tableName: tableName,
        defaultFilter: {
            search: "",
            zahllaufId: request.filter.zahllaufId,
        }
    });

    useEffect(() => {
        setFilter({
            search: currentCustomFilter.search,
            zahllaufId: currentCustomFilter.zahllaufId,
            filtered: filteredStateFilter || undefined,
        });
    }, [currentCustomFilter, filteredStateFilter, setFilter]);

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                position: "relative",
                width: "100%",
            }}
        >
            <SearchField
                value={currentCustomFilter.search}
                onChange={(val) => setCustomField("search", val)}
            />
        </Box>
    );
}

function ZahllaufRechnungenResults({
                                       setOpenedModal,
                                       request,
                                       setFilteredState,
                                   }: {
    setOpenedModal: (value: boolean) => void;
    request: DataRequestState<ZahllaufRechnungenModalFilter>;
    setFilteredState: (value: string) => void;
}) {
    return <Results setOpenedModal={setOpenedModal} request={request} setFilteredState={setFilteredState}/>;
}

function Results({
                     setOpenedModal,
                     request,
                     setFilteredState,
                 }: {
    setOpenedModal: (value: boolean) => void;
    request: DataRequestState<ZahllaufRechnungenModalFilter>;
    setFilteredState: (value: string) => void;
}) {
    const getApi = useGetApi();
    const navigate = useNavigate();

    const ladeRechnungenZuZahllauf: DataLoader<
        ZahllaufRechnungenModalFilter,
        EingangsrechnungenProps
    > = useCallback(
        async (params) =>
            (await getApi()).zahllaeufe.sucheRechnungenZuZahllauf({...params}),
        [getApi]
    );

    return (
        <>
            <DataTable
                columns={useColumns()}
                request={request}
                queryKey={[tableName]}
                loadData={ladeRechnungenZuZahllauf}
                onRowClick={(row) => {
                    setOpenedModal(false);
                    navigate(`/eingangsrechnungen/${row.id}`);
                }}
                name={tableName}
                setFiltered={setFilteredState}
            />
        </>
    );
}

function useColumns(): Array<GridColDef<EingangsrechnungenProps>> {
    const {t: translation} = useT("zahllauf");

    return useMemo(
        () => [
            {
                field: "zahlungkw",
                headerName: capitalize(translation("calendar-week-short")),
                flex: 0.5,
                type: "number",
            },
            {
                field: "skontoDatum",
                headerName: capitalize(translation("discount-due-date")),
                flex: 1,
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: ({value}) => {
                    return value != "Invalid Date"
                        ? new Date(value).toLocaleDateString("de-DE", {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                        })
                        : translation("no-date");
                },
                type: "date",
            },
            {
                field: "faelligOhneSkonto",
                headerName: capitalize(translation("due-date")),
                flex: 1,
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: ({value}) => {
                    return value != "Invalid Date"
                        ? new Date(value).toLocaleDateString("de-DE", {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                        })
                        : capitalize(translation("no-date"));
                },
                type: "date",
            },
            {
                field: "lieferantName",
                headerName: capitalize(translation("supplier")),
                flex: 1,
                type: "string",
            },
            {
                field: "rechnungsNummer",
                headerName: capitalize(translation("invoice-number")),
                flex: 1,
                type: "string",
            },
            {
                field: "gutschriftenRechnungsnummern",
                headerName: capitalize(translation("credits")),
                flex: 1,
                type: "string",
            },
            {
                field: "rechnungsDatum",
                headerName: capitalize(translation("invoice-date")),
                type: "date",
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: ({value}) =>
                    value != "Invalid Date"
                        ? new Date(value).toLocaleDateString("de-DE", {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                        })
                        : capitalize(translation("no-date")),
                width: 150,
            },
            {
                field: "belegStatus",
                headerName: capitalize(translation("document-status")),
                renderCell: (params) => {
                    const id = params.row.id;
                    switch (params.value) {
                        case "IN_ERKENNUNG":
                            return (
                                <Chip
                                    icon={<TextIncreaseIcon/>}
                                    title={"In Erkennung"}
                                    label={"in Erkennung"}
                                ></Chip>
                            );
                        case "OFFEN":
                            return (
                                <Chip
                                    icon={<SpellcheckIcon/>}
                                    title={"Offen"}
                                    color={"secondary"}
                                    label={"offen"}
                                ></Chip>
                            );
                        case "IN_PRUEFUNG":
                            return (
                                <Chip
                                    icon={<PlaylistAddCheckCircleIcon/>}
                                    title={"In Prüfung"}
                                    color={"warning"}
                                    label={"in Prüfung"}
                                ></Chip>
                            );
                        case "FREIGEGEBEN":
                            return (
                                <Chip
                                    icon={<TaskAltIcon/>}
                                    title={"Freigegeben"}
                                    color={"success"}
                                    label={"freigegeben"}
                                ></Chip>
                            );
                        case "ABGELEHNT":
                            return (
                                <Chip
                                    icon={<HighlightOffIcon/>}
                                    title={"Abgelehnt"}
                                    color={"error"}
                                    label={"abgelehnt"}
                                ></Chip>
                            );
                        case "IN_DATEV":
                            return (
                                <DatevProtokolleButton
                                    id={id}
                                    label={"in DATEV"}
                                    color={"success"}
                                    title={"Verarbeitet durch DATEV"}
                                />
                            );
                        case "VERARBEITUNG_DATEV":
                            return (
                                <DatevProtokolleButton
                                    id={id}
                                    label={"Verarbeitung DATEV"}
                                    color={"warning"}
                                    title={"Verarbeitung durch DATEV"}
                                />
                            );
                        case "FEHLER_BEI_UEBERTRAG_DATEV":
                            return (
                                <DatevProtokolleButton
                                    id={id}
                                    label={"Fehler DATEV"}
                                    color={"error"}
                                    title={"Fehler bei Übertrag nach Datev"}
                                />
                            );
                        case "FEHLER_GOOGLE":
                            return (
                                <DatevProtokolleButton
                                    id={id}
                                    label={"Fehler Erkennung"}
                                    color={"warning"}
                                    title={"Fehler Erkennung Google"}
                                />
                            );
                        case "BEZAHLT":
                            return (
                                <Chip
                                    icon={<PaidIcon/>}
                                    title={"Bezahlt"}
                                    color={"success"}
                                    label={"bezahlt"}
                                ></Chip>
                            );
                        case "STORNIERT":
                            return (
                                <Chip
                                    icon={<Block/>}
                                    title={"Storniert"}
                                    color={"secondary"}
                                    label={"storniert"}
                                ></Chip>
                            );
                    }
                },
                flex: 1,
            },
            {
                field: "nettoBetrag",
                headerName: capitalize(translation("payment-amount-net")),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                renderCell: ({value}) => {
                    return formatAsMoney(value);
                },
            },
            {
                field: "umsatzsteuer",
                headerName: capitalize(translation("tax")),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                renderCell: ({value}) => {
                    return formatAsMoney(value);
                },
            },
            {
                field: "bruttoBetrag",
                headerName: capitalize(translation("payment-amount-gross")),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                renderCell: ({value}) => {
                    return formatAsMoney(value);
                },
            },
            {
                field: "skontoBetrag",
                headerName: capitalize(translation("discount-amount")),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                renderCell: ({value}) => {
                    return formatAsMoney(value);
                },
            },
            {
                field: "rechnungsBetrag",
                headerName: capitalize(translation("payment-amount")),
                flex: 1,
                align: "right",
                headerAlign: "right",
                type: "number",
                renderCell: ({value}) => {
                    return formatAsMoney(value);
                },
            },
            {
                field: "dateiErstelltAm",
                headerName: capitalize(translation("receipt-date")),
                flex: 1,
                valueGetter: (value) => {
                    return new Date(value);
                },
                renderCell: ({value}) => {
                    return value != "Invalid Date"
                        ? new Date(value).toLocaleDateString("de-DE", {
                            month: "2-digit",
                            day: "2-digit",
                            year: "numeric",
                        })
                        : capitalize(translation("no-date"));
                },
                type: "date",
            },
        ],
        [translation]
    );
}

export default ZahllaufRechnungenModal;
